// The landing page's second screen: "What your AI agent does", four big
// lines (founder, 2026-11-02: agent-specific, because that is what people buy).
//
// One line is lit at a time, with its sentence under it (the founder's
// reference: Perplexity's hub page, one idea per screen, big type). On a
// computer the lighting follows the scroll through a short pinned stretch, and
// a click lights a line straight away. The old version pinned a 320vh stage
// around a rounded panel and left that panel empty at the end; this one is
// about 80vh of scroll and has no panel at all.
//
// On a phone, a short window, or with reduced motion asked for, nothing is
// pinned or animated: all four lines show, each with its sentence.
import React, { useCallback, useEffect, useRef, useState } from 'react';

export const WWD_HEADING = 'What your AI agent does';

export const WWD_LINES = [
  {
    id: 'fit',
    title: 'Finds jobs that fit you',
    say: 'It reads nearly 900,000 openings and ranks the ones that match your resume.',
  },
  {
    id: 'resume',
    title: 'Tailors your resume',
    say: 'It rewrites your resume for each job, using only what you’ve done.',
  },
  {
    id: 'filled',
    title: 'Fills every application',
    say: 'It reads each form and fills it in your browser.',
  },
  {
    id: 'checks',
    title: 'Checks before it sends',
    say: 'It reads every answer back, and asks you only what it can’t know.',
  },
];

// The pinned stretch is worth it only with room to pin in and motion allowed.
// Mirrors the CSS, which keys off the class this sets rather than its own
// media query, so the two can never disagree.
const LIVE_QUERY = '(min-width: 769px) and (min-height: 600px) and (prefers-reduced-motion: no-preference)';

function canPin() {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') return window.matchMedia(LIVE_QUERY).matches;
  return window.innerWidth > 768 && window.innerHeight >= 600;
}

function usePinnable() {
  const [live, setLive] = useState(canPin);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(LIVE_QUERY);
    const sync = () => setLive(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else if (mq.removeListener) mq.removeListener(sync);
    };
  }, []);
  return live;
}

// The app scrolls inside a layout pane on a computer, not the window.
function scrollerOf(node) {
  let parent = node?.parentElement;
  while (parent && parent !== document.body) {
    if (/(auto|scroll)/.test(window.getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

export default function LandingWhatWeDo() {
  const live = usePinnable();
  const [lit, setLit] = useState(0);
  const sectionRef = useRef(null);
  const pinRef = useRef(null);

  // How far the pinned block has travelled down its section, 0 to 1. Before
  // it pins it sits at the top (0); once the section scrolls past, at the
  // bottom (1). No viewport arithmetic, so the window and the pane agree.
  const travel = useCallback(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    if (!section || !pin) return null;
    const s = section.getBoundingClientRect();
    const p = pin.getBoundingClientRect();
    const room = s.height - p.height;
    if (room <= 0) return null;
    return { s, p, room, progress: Math.max(0, Math.min(1, (p.top - s.top) / room)) };
  }, []);

  useEffect(() => {
    if (!live) return undefined;
    let frame = null;
    const update = () => {
      frame = null;
      const t = travel();
      if (!t) return;
      const next = Math.min(WWD_LINES.length - 1, Math.floor(t.progress * WWD_LINES.length));
      setLit((prev) => (prev === next ? prev : next));
    };
    const schedule = () => { if (frame == null) frame = window.requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    update();
    return () => {
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [live, travel]);

  // A click lights the line now, and scrolls to the middle of its stretch so
  // the next scroll does not snap the light back to where it was.
  const pick = (index) => {
    setLit(index);
    const t = travel();
    const pin = pinRef.current;
    if (!t || !pin) return;
    const scroller = scrollerOf(sectionRef.current);
    const top = scroller === window ? 0 : scroller.getBoundingClientRect().top;
    const stick = parseFloat(window.getComputedStyle(pin).top) || 0;
    const want = ((index + 0.5) / WWD_LINES.length) * t.room;
    const delta = t.s.top - (top + stick - want);
    if (typeof scroller.scrollBy === 'function') scroller.scrollBy({ top: delta, behavior: 'smooth' });
  };

  return (
    <section
      className={`lp-wwd${live ? ' is-live' : ''}`}
      id="what-we-do"
      ref={sectionRef}
      aria-labelledby="wwd-title"
    >
      <div className="lp-wwd-pin" ref={pinRef}>
        <h2 id="wwd-title" className="lp-wwd-heading">{WWD_HEADING}</h2>
        <ul className="lp-wwd-lines">
          {WWD_LINES.map((line, index) => {
            const on = !live || lit === index;
            return (
              <li key={line.id} className={`lp-wwd-line${on ? ' is-on' : ''}`}>
                <h3 className="lp-wwd-title">
                  {live ? (
                    <button
                      type="button"
                      aria-expanded={on}
                      aria-controls={`wwd-say-${line.id}`}
                      onClick={() => pick(index)}
                    >
                      {line.title}
                    </button>
                  ) : line.title}
                </h3>
                <div className="lp-wwd-say-wrap">
                  <p id={`wwd-say-${line.id}`} className="lp-wwd-say">{line.say}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
